'use client';

import type { Lead } from '@up2stay/up2promo-api-client';
import { formatLeadDisplayName } from '@up2stay/up2promo-commons';
import { Card, cn } from '@up2stay/up2promo-ui';
import { ContactRound, Lock, Users } from 'lucide-react';

import { LeadStatusBadge } from '@/core/components/leads/lead-detail/lead-status-badge';
import {
  formatLeadCapturedAt,
  leadCompanyLine,
  leadInitials,
  personInitials,
} from '@/core/components/leads/leads-list-display';
import { ListEmptyState } from '@/core/components/layout';

const DESKTOP_GRID =
  'grid grid-cols-[minmax(0,2.6fr)_110px_minmax(0,1fr)_minmax(0,1fr)_90px_90px] gap-3';

type LeadEventLeadsTableProps = {
  leads: Lead[];
  locale: string;
  statusLabels: Record<string, string>;
  sourceLabels: Record<string, string>;
  resolveAssigneeName: (userId: string | null | undefined) => string;
  resolveCreatorName: (lead: Lead) => string;
  title: string;
  privateAdminHint?: string | null;
  emptyTitle: string;
  emptyDescription: string;
  columnLabels: {
    contact: string;
    status: string;
    source: string;
    assignee: string;
    visibility: string;
    captured: string;
    workspace: string;
    private: string;
  };
  onLeadClick: (lead: Lead) => void;
};

export function LeadEventLeadsTable({
  leads,
  locale,
  statusLabels,
  sourceLabels,
  resolveAssigneeName,
  resolveCreatorName,
  title,
  privateAdminHint,
  emptyTitle,
  emptyDescription,
  columnLabels,
  onLeadClick,
}: LeadEventLeadsTableProps) {
  const privateCount = leads.filter((l) => l.visibility === 'private').length;

  if (leads.length === 0) {
    return (
      <Card className="border-[color:var(--surface-border)] shadow-[var(--shadow-card)]">
        <div className="p-6">
          <ListEmptyState
            icon={<ContactRound className="h-6 w-6" aria-hidden />}
            title={emptyTitle}
            description={emptyDescription}
          />
        </div>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden border-[color:var(--surface-border)] shadow-[var(--shadow-card)]">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-[color:var(--surface-border)] px-4 py-3 sm:px-5">
        <h3 className="text-[15.5px] font-bold leading-tight">{title}</h3>
        {privateCount > 0 && privateAdminHint ? (
          <p className="text-[12px] text-muted-foreground">
            {privateAdminHint.replace('{count}', String(privateCount))}
          </p>
        ) : null}
      </div>

      <div className="hidden md:block">
        <div
          className={cn(
            DESKTOP_GRID,
            'border-b border-[color:var(--surface-border)] px-4 py-2.5 text-[11.5px] text-muted-foreground sm:px-5',
          )}
        >
          <div>{columnLabels.contact}</div>
          <div>{columnLabels.status}</div>
          <div>{columnLabels.source}</div>
          <div>{columnLabels.assignee}</div>
          <div>{columnLabels.visibility}</div>
          <div>{columnLabels.captured}</div>
        </div>
        {leads.map((lead) => {
          const name =
            formatLeadDisplayName(lead.firstName, lead.lastName) ||
            lead.email ||
            '—';
          const company = leadCompanyLine(lead, false);
          const assignee = resolveAssigneeName(lead.assigneeUserId);
          const creator = resolveCreatorName(lead);
          const source = sourceLabels[lead.source] ?? lead.source;
          const isPrivate = lead.visibility === 'private';

          return (
            <button
              key={lead.id}
              type="button"
              onClick={() => onLeadClick(lead)}
              className={cn(
                DESKTOP_GRID,
                'w-full items-center px-4 py-3 text-left hover:bg-muted/40 sm:px-5',
                'border-t border-[color:var(--surface-border)] first:border-t-0',
              )}
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-bold">
                  {leadInitials(lead)}
                </span>
                <div className="min-w-0">
                  {/* Two lines so similar names stay apart; full name on
                      hover when it is still cut (DEV-820). */}
                  <div
                    className="line-clamp-2 text-[13.5px] font-semibold [overflow-wrap:anywhere]"
                    title={name}
                  >
                    {name}
                  </div>
                  {company ? (
                    <div
                      className="truncate text-[12px] text-muted-foreground"
                      title={company}
                    >
                      {company}
                    </div>
                  ) : null}
                </div>
              </div>
              <LeadStatusBadge
                status={lead.status}
                label={statusLabels[lead.status] ?? lead.status}
              />
              <div className="min-w-0 truncate text-[12.5px] text-muted-foreground">
                <span className="truncate">{source}</span>
                {creator ? (
                  <span className="mt-0.5 flex items-center gap-1 truncate">
                    <span className="inline-flex size-4 items-center justify-center rounded-full bg-[color:var(--ghost-accent-bg)] text-[8px] font-bold text-[color:var(--ghost-accent-fg)]">
                      {personInitials(creator)}
                    </span>
                    <span className="truncate" title={creator}>
                      {creator}
                    </span>
                  </span>
                ) : null}
              </div>
              <div className="flex min-w-0 items-center gap-2 truncate text-[12.5px] text-muted-foreground">
                {assignee ? (
                  <>
                    <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-[color:var(--ghost-accent-bg)] text-[9px] font-bold text-[color:var(--ghost-accent-fg)]">
                      {personInitials(assignee)}
                    </span>
                    <span className="truncate" title={assignee}>
                      {assignee}
                    </span>
                  </>
                ) : (
                  '—'
                )}
              </div>
              <div className="inline-flex items-center gap-1 text-[12px] text-muted-foreground">
                {isPrivate ? (
                  <>
                    <Lock className="size-3.5" aria-hidden />
                    {columnLabels.private}
                  </>
                ) : (
                  <>
                    <Users className="size-3.5" aria-hidden />
                    {columnLabels.workspace}
                  </>
                )}
              </div>
              <div className="text-[12px] text-muted-foreground">
                {formatLeadCapturedAt(lead.createdAt, locale)}
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col md:hidden">
        {leads.map((lead) => {
          const name =
            formatLeadDisplayName(lead.firstName, lead.lastName) ||
            lead.email ||
            '—';
          return (
            <button
              key={lead.id}
              type="button"
              onClick={() => onLeadClick(lead)}
              className="flex items-center gap-3 border-t border-[color:var(--surface-border)] px-4 py-3 text-left first:border-t-0"
            >
              <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-bold">
                {leadInitials(lead)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[14px] font-semibold">{name}</div>
                <div className="mt-1">
                  <LeadStatusBadge
                    status={lead.status}
                    label={statusLabels[lead.status] ?? lead.status}
                  />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </Card>
  );
}
